import { useCallback, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { Add, Remove } from '@mui/icons-material';
import { Box, Typography, Button, Stack } from '@mui/material';
import { EditableDataTable } from './editable-data-table';
import { useViewContractsGrowthIncentiveTiersTableInstance } from './view-contracts-growth-incentive-tiers-table-instance';
import { GrowthTier, CombinedTier } from 'shared/types';

interface Props {
  isEditMode: boolean;
  isDiffingMode: boolean;
  isDraft: boolean;
  isRejected: boolean;
  comparingContractTiers?: GrowthTier[];
}

export function ViewContractsGrowthIncentiveTiersTableContainer({
  isEditMode,
  isDiffingMode,
  isDraft,
  isRejected,
  comparingContractTiers,
}: Props) {
  const watchedFields = useWatch({ name: 'growthTiers' });
  const { fields, append, remove } = useFieldArray<{ growthTiers: GrowthTier[] }>({
    name: 'growthTiers',
  });
  const controlledFields = useMemo(
    () =>
      fields.map((field, index) => ({
        ...field,
        ...watchedFields[index],
      })),
    [fields, watchedFields]
  );

  const combinedContractFields = useMemo(() => {
    const combinedContractFieldsArray: CombinedTier[] = [];
    const maxTier = Math.max(controlledFields.length, comparingContractTiers ? comparingContractTiers?.length : 0);

    for (let i = 1; i <= maxTier; i++) {
      const currentTierInfo: CombinedTier = { tier: i.toString() };
      controlledFields.forEach(field => {
        if (currentTierInfo.tier === field.tier) {
          currentTierInfo.rebate = field.rebate;
          currentTierInfo.floor = field.floor;
        }
      });
      comparingContractTiers?.forEach(field => {
        if (currentTierInfo.tier === field.tier) {
          currentTierInfo.diffingRebate = field.rebate;
          currentTierInfo.diffingFloor = field.floor;
        }
      });

      if (currentTierInfo.rebate !== undefined || currentTierInfo?.diffingRebate !== undefined) {
        combinedContractFieldsArray.push(currentTierInfo);
      }
    }
    return combinedContractFieldsArray;
  }, [controlledFields, comparingContractTiers]);

  const hasSevenTiers = fields.length === 7;
  const hasNoTiers = fields.length === 0;

  const addTier = useCallback(() => {
    const highestTier = fields.slice(-1)[0];

    const tier = highestTier ? `${Number(highestTier.tier) + 1}` : '1';

    append({
      tier,
      floor: 0,
      rebate: 0,
    });
  }, [fields, append]);
  const removeTier = useCallback(() => {
    const lastIndex = fields.length - 1;
    if (lastIndex > -1) {
      remove(lastIndex);
    }
  }, [remove, fields]);

  const rejectedAndEditMode = isEditMode && isRejected;
  const fieldsForTable = (isDiffingMode || isDraft) && !rejectedAndEditMode ? combinedContractFields : controlledFields;
  const contractGrowthTiersTableInstance = useViewContractsGrowthIncentiveTiersTableInstance(
    fieldsForTable,
    isDiffingMode,
    isDraft,
    rejectedAndEditMode
  );
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, pt: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
          Growth Incentive
        </Typography>
        {isEditMode && (
          <Stack spacing={1} direction='row'>
            <Box>
              <Button
                disabled={hasNoTiers}
                variant='outlined'
                color='primary'
                startIcon={<Remove />}
                onClick={removeTier}
              >
                remove tier
              </Button>
            </Box>
            <Box>
              <Button disabled={hasSevenTiers} variant='outlined' color='primary' startIcon={<Add />} onClick={addTier}>
                add tier
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
      <EditableDataTable
        instance={contractGrowthTiersTableInstance}
        noDataMessage='No growth tiers have been entered.'
        isDraft={isDraft}
        isDiffingMode={isDiffingMode}
        rejectedAndEditMode={rejectedAndEditMode}
      />
    </>
  );
}
